"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  PictureAsPdf,
  Close,
  CheckCircle,
  Description,
  InsertDriveFile,
} from "@mui/icons-material";

interface WordFile {
  file: File;
  id: string;
}

export default function WordToPdfConverter() {
  const [wordFile, setWordFile] = useState<WordFile | null>(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const validTypes = [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const fileName = file.name.toLowerCase();
    const isValidExtension =
      fileName.endsWith(".doc") || fileName.endsWith(".docx");

    if (validTypes.includes(file.type) || isValidExtension) {
      setWordFile({
        file,
        id: Math.random().toString(36).substr(2, 9),
      });
      setError(null);
      setSuccess(false);
    } else {
      setError(
        `${file.name} is not a supported format. Please use DOC or DOCX files.`
      );
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setWordFile(null);
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "word-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const convertToPdf = async () => {
    if (!wordFile) {
      setError("Please select a Word document to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", wordFile.file);

      const response = await axios.post(
        process.env.NEXT_PUBLIC_API_URL + "/word-to-pdf",
        formData,
        {
          timeout: 600000,
          responseType: "blob",
        }
      );

      // Get the PDF blob
      const blob = new Blob([response.data]);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const originalName = wordFile.file.name.replace(
        /\.(docx?|DOC[X]?)$/i,
        ""
      );
      a.download = `${originalName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(true);

      // Clear file after successful conversion
      setTimeout(() => {
        removeFile();
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to convert Word document to PDF";
      if (axios.isAxiosError(err) && err.response) {
        try {
          const contentType = String(err.response.headers["content-type"] ?? "");
          if (contentType.includes("application/json")) {
            const errorText = await new Response(err.response.data).text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Server error: ${err.response.status}`;
          }
        } catch {
          errorMessage = err.message || errorMessage;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("word-file-input")?.click()}
        >
          <input
            id="word-file-input"
            type="file"
            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your Word document here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported formats: DOC, DOCX
          </Typography>

          <Button
            variant="contained"
            startIcon={<Description sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("word-file-input")?.click();
            }}
          >
            Select Word Document
          </Button>
        </Box>

        {/* Selected File Preview */}
        {wordFile && (
          <Fade in={!!wordFile}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected Document
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={removeFile}
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </Box>

              <Zoom in>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: "#2B579A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 32, color: "white" }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {wordFile.file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(wordFile.file.size)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={removeFile}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Paper>
              </Zoom>
            </Box>
          </Fade>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            PDF created successfully! Your download should start automatically.
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<PictureAsPdf sx={{ color: "#ffffff" }} />}
            onClick={convertToPdf}
            disabled={converting || !wordFile}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to PDF"}
          </Button>

          {wordFile && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<Description />}
                label="Word Document"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={formatFileSize(wordFile.file.size)}
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
